import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '../../components';
import { Edit, Trash } from '../../components/icons';
import { IconButton } from '@mui/material';
import { parse, stringify } from 'flatted';
import parsePhoneNumberFromString from "libphonenumber-js";

interface FormData {
  name?: string;
  field_type?: string;
  field_properties?: {
    table_rows?: any[];
    [key: string]: any;
  };
  disableDefaultActionColumn?: boolean;
  defaultActionColumnItems?: any;
  [key: string]: any;
}

export interface UseAddressContactOptions {
  isEdit?: boolean;
  initialAddressItems?: any[];
  initialContactItems?: any[];
  baseFields?: any[];
  setFormFields: (fields: any[]) => void;
  filterKsaColumns: (fields: any[]) => any[];
  formValues?: Record<string, any>;
  formMethods?: any;
  fieldPrefix?: string;
}

export interface UseAddressContactReturn {
  addressItems: any[];
  contactItems: any[];
  popupFormFieldObject: any;
  setPopupFormFieldObject: React.Dispatch<React.SetStateAction<any>>;
  itemToEdit: any;
  setItemToEdit: React.Dispatch<React.SetStateAction<any>>;
  deleteFieldObject: any;
  setDeleteFieldObject: React.Dispatch<React.SetStateAction<any>>;
  handleAddressItemChange: (data: any, itemIndex: any) => Promise<void>;
  handleContactItemChange: (data: any, itemIndex: any) => Promise<void>;
  handleAddressRemove: () => Promise<void>;
  handleContactRemove: () => Promise<void>;
  changeFormData: (fieldName?: any, fieldProp?: any, propOptions?: any, fields?: any[]) => void;
}

const normalizeContactNumber = (
  rawNumber?: string,
  fallbackCountryCode?: string
) => {
  if (!rawNumber) return { contact_no: "", country_code: "" };
  const parsed = parsePhoneNumberFromString(`+${rawNumber}`);
  if (!parsed?.countryCallingCode) {
    return { contact_no: rawNumber, country_code: fallbackCountryCode || "" };
  }
  return {
    contact_no: parsed.nationalNumber || rawNumber,
    country_code: `+${parsed.countryCallingCode}`,
  };
};

export function useAddressContact(options: UseAddressContactOptions): UseAddressContactReturn {
  const {
    isEdit = false,
    initialAddressItems = [],
    initialContactItems = [],
    baseFields = [],
    setFormFields,
    filterKsaColumns,
    formValues,
    formMethods,
    fieldPrefix = '',
  } = options;

  const [addressItems, setAddressItems] = useState<any[]>(initialAddressItems);
  const [contactItems, setContactItems] = useState<any[]>(initialContactItems);
  const [popupFormFieldObject, setPopupFormFieldObject] = useState<any>({});
  const [itemToEdit, setItemToEdit] = useState<any>(isEdit ? {} : null);
  const [deleteFieldObject, setDeleteFieldObject] = useState<any>({});

  useEffect(() => {
    if (isEdit) {
      if (!addressItems?.length && initialAddressItems?.length) {
        setAddressItems(initialAddressItems);
      }
      if (!contactItems?.length && initialContactItems?.length) {
        setContactItems(initialContactItems);
      }
    }
  }, [initialAddressItems, initialContactItems, isEdit]);

  const actionColumns = useMemo(() => {
    const createActionColumns = (field: any) => {
      return [
        <IconButton
          onClick={({ row }: any) => {
            setPopupFormFieldObject({ ...field, row });
            setItemToEdit(row.index);
          }}
        >
          <Edit fontSize="small" htmlColor="#292D32" />
        </IconButton>,
        <IconButton
          onClick={({ row }: any) => {
            setDeleteFieldObject({ ...field, row });
          }}
        >
          <Trash fontSize="small" htmlColor="#292D32" />
        </IconButton>,
      ];
    };
    return createActionColumns;
  }, []);

  const handleAddressCheckboxRows = useCallback((rows: any[]) => {
    return rows?.map((row: any) => {
      const dba = Boolean(row?.default_billing_address);
      const dsa = Boolean(row?.default_shipping_address);
      return {
        ...row,
        default_billing_address: <Checkbox checked={dba} />,
        default_shipping_address: <Checkbox checked={dsa} />,
      };
    });
  }, []);

  const changeFormData = useCallback(
    (fieldName: any = "", fieldProp: any = null, propOptions: any = null, fields: any = []) => {
      const updateTableRows = (field: FormData) => {
        if (field?.field_type === "table") {
          if (field.name === "addresses") {
            field.field_properties = {
              ...field.field_properties,
              table_rows: handleAddressCheckboxRows(addressItems),
            };
          }
          if (field.name === "contacts") {
            field.field_properties = {
              ...field.field_properties,
              table_rows: contactItems,
            };
          }
          if (field.name === "bank_accounts") {
            field.disableDefaultActionColumn = true;
          }
          if (field.name === "transactions") {
            field.disableDefaultActionColumn = true;
          }
          field.defaultActionColumnItems = actionColumns({ ...field });
        }
      };

      const sourceFields = fields?.length ? fields : baseFields;
      const parsedFormData: FormData[] = parse(stringify(filterKsaColumns(sourceFields)));
      const data = parsedFormData
        ?.map((field) => {
          updateTableRows(field);
          if (fieldName === field.name && fieldProp) {
            field = {
              ...field,
              ...{
                ...fieldProp,
                field_properties: {
                  ...field.field_properties,
                  ...fieldProp.field_properties,
                },
              },
            };
          }
          return field;
        })
        ?.filter((field) => field.field_type !== "relation");

      if (data?.length && stringify(baseFields) !== stringify(data)) {
        setFormFields(data);
      }
    },
    [actionColumns, addressItems, baseFields, contactItems, filterKsaColumns, handleAddressCheckboxRows, setFormFields]
  );

  useEffect(() => {
    if (isEdit && initialAddressItems?.length) {
      const dataWithJsx = handleAddressCheckboxRows(initialAddressItems);
      changeFormData("addresses", {
        field_properties: { table_rows: dataWithJsx },
      });
    }
  }, []);

  const handleAddressItemChange = async (data: any, itemIndex: any) => {
    const addUpdateRow = (array: any[], itemIndex: number) => {
      const existed = array.find((_, index) => itemIndex === index);
      if (existed) {
        return array.map((arrValue, index) =>
          itemIndex === index ? { ...arrValue, ...data.addresses } : { ...arrValue }
        );
      } else {
        return [...array, ...[data.addresses]];
      }
    };
    const addressesData = addUpdateRow(addressItems, itemIndex);

    setAddressItems(addressesData);
    setItemToEdit(null);
    const dataWithJsx = handleAddressCheckboxRows(addressesData);

    changeFormData("addresses", {
      field_properties: { table_rows: dataWithJsx },
    });
  };

  const handleContactItemChange = async (data: any, itemIndex: any) => {
    if (isEdit) {
      let newItem = true;
      const contactsData: any[] = [];
      contactItems?.forEach((item: any, index: number) => {
        if (index === itemIndex) {
          newItem = false;
          item = {
            ...item.contacts,
            ...data.contacts,
          };
        }
        contactsData?.push(item);
      });
      if (newItem) {
        contactsData?.push(data.contacts);
      }
      setContactItems(contactsData);
      setItemToEdit(null);
      changeFormData("contacts", {
        field_properties: { table_rows: contactsData },
      });
    } else {
      const contactData = data.contacts || data;
      let updatedContacts;
      if (itemIndex !== null && itemIndex !== undefined) {
        updatedContacts = contactItems.map((item: any, index: number) =>
          index === itemIndex ? { ...item, ...contactData } : item
        );
      } else {
        updatedContacts = [...contactItems, contactData];
      }
      setContactItems(updatedContacts);
      setItemToEdit(null);
      changeFormData('contacts', {
        field_properties: { table_rows: updatedContacts },
      });

      const updatedPrimaryContact = updatedContacts[0] || {};
      if (formMethods?.setValue && fieldPrefix) {
        const nextEmailIds = (() => {
          const firstEmail = (updatedPrimaryContact?.email || '').trim();
          const emails = String(formValues?.email_ids || '')
            .split(',')
            .map((e: string) => e.trim())
            .filter(Boolean);
          if (!emails.length) return firstEmail;
          if (emails.length === 1) return firstEmail;
          if (!firstEmail) return emails.slice(1).join(', ');
          emails[0] = firstEmail;
          return emails.join(', ');
        })();

        if ((formValues?.responsible_person || "") !== (updatedPrimaryContact?.name || "")) {
          formMethods.setValue(`${fieldPrefix}.responsible_person`, updatedPrimaryContact?.name || "");
        }
        if ((formValues?.contact_no || "") !== (updatedPrimaryContact?.contact_no || "")) {
          formMethods.setValue(`${fieldPrefix}.contact_no`, updatedPrimaryContact?.contact_no || "");
        }
        if ((formValues?.email_ids || "") !== nextEmailIds) {
          formMethods.setValue(`${fieldPrefix}.email_ids`, nextEmailIds);
        }
      }
    }
  };

  const handleAddressRemove = async () => {
    const filteredAddress = addressItems.filter(
      (_: any, index: number) => deleteFieldObject?.row?.index !== index
    );
    setAddressItems(filteredAddress);
    const dataWithJsx = handleAddressCheckboxRows(filteredAddress);
    setDeleteFieldObject(null);
    changeFormData("addresses", {
      field_properties: { table_rows: dataWithJsx },
    });
  };

  const handleContactRemove = async () => {
    const filteredContacts = contactItems.filter(
      (_: any, index: number) => deleteFieldObject?.row?.index !== index
    );
    setContactItems(filteredContacts);
    setDeleteFieldObject(null);
    changeFormData("contacts", {
      field_properties: { table_rows: filteredContacts },
    });
  };

  useEffect(() => {
    if (isEdit) return;
    if (!formValues) return;

    const getFirstEmail = (emailIds: string = "") =>
      emailIds.split(",").map((email) => email.trim()).filter(Boolean)[0] || "";

    const primaryName = formValues?.responsible_person || "";
    const primaryEmail = getFirstEmail(String(formValues?.email_ids || ""));
    const { contact_no: primaryContactNo, country_code: primaryCountryCode } =
      normalizeContactNumber(formValues?.contact_no, formValues?.country_code);
    const currentPrimaryContact = contactItems?.[0] || {};

    if (!primaryName && !primaryContactNo && !primaryEmail && !contactItems?.length) return;

    const nextPrimaryContact = {
      ...currentPrimaryContact,
      name: primaryName,
      contact_no: primaryContactNo,
      country_code: primaryCountryCode || currentPrimaryContact?.country_code || "",
      email: primaryEmail,
    };

    if (
      (currentPrimaryContact?.name || "") === nextPrimaryContact.name &&
      (currentPrimaryContact?.contact_no || "") === nextPrimaryContact.contact_no &&
      (currentPrimaryContact?.country_code || "") === nextPrimaryContact.country_code &&
      (currentPrimaryContact?.email || "") === nextPrimaryContact.email
    ) return;

    const updatedContacts = contactItems?.length
      ? contactItems.map((item, index) => (index === 0 ? nextPrimaryContact : item))
      : [nextPrimaryContact];

    setContactItems(updatedContacts);
    changeFormData("contacts", {
      field_properties: { table_rows: updatedContacts },
    });
  }, [
    changeFormData,
    contactItems,
    formValues?.contact_no,
    formValues?.country_code,
    formValues?.email_ids,
    formValues?.responsible_person,
    isEdit,
  ]);

  return {
    addressItems,
    contactItems,
    popupFormFieldObject,
    setPopupFormFieldObject,
    itemToEdit,
    setItemToEdit,
    deleteFieldObject,
    setDeleteFieldObject,
    handleAddressItemChange,
    handleContactItemChange,
    handleAddressRemove,
    handleContactRemove,
    changeFormData,
  };
}

export default useAddressContact;
